import React from "react";
import {
  Box,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { publicAxios as axios } from "../../../config/axios";
import endpoints from "../../../utils/endpoints";
import { CboDocument, formatDispatchDate } from "../../../components/SupplierInventorySearch/CboDetailsModal";

const getDispatchTotal = (row: CboDocument) =>
  (row.dispatched || []).reduce(
    (sum, item) => sum + Number(item?.dispatchedQuantity || 0),
    0,
  );

const getDispatchedDisplay = (row: CboDocument) => {
  const entries = (row.dispatched || [])
    .filter(
      (dispatchItem) =>
        Boolean(dispatchItem.gatePassDate) ||
        typeof dispatchItem.dispatchedQuantity === "number",
    )
    .map((dispatchItem) => {
      const dateLabel =
        formatDispatchDate(dispatchItem.gatePassDate) || "Unknown";
      const quantity = dispatchItem.dispatchedQuantity || 0;
      return `${dateLabel} ~ ${quantity}`;
    });

  if (entries.length > 0) {
    return entries.join(" | ");
  }
  return "—";
};

const withPartNumberRowSpan = (rows: CboDocument[]) => {
  const partNumberCounts = rows.reduce(
    (acc, row) => {
      const key = row.partNumber || "—";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const seen: Record<string, number> = {};
  return rows.map((row) => {
    const key = row.partNumber || "—";
    seen[key] = (seen[key] || 0) + 1;
    const isFirst = seen[key] === 1;
    return {
      row,
      showPartNumber: isFirst,
      rowSpan: isFirst ? partNumberCounts[key] : 0,
    };
  });
};

const CboDetailsPopoverContent = ({
  rootPartNumber,
  partNumber,
}: {
  rootPartNumber: string;
  partNumber: string;
}) => {
  const { data, isFetching, error } = useQuery(
    ["cbo-popover-by-root-part", rootPartNumber],
    async () => {
      const { data: response } = await axios.get(
        endpoints.getCboByPartNumbers([rootPartNumber]),
        {
          headers: {
            Authorization: localStorage.getItem("auth_orca"),
          },
        },
      );

      const rawResults = Array.isArray(response?.results)
        ? response.results
        : Array.isArray(response?.data?.results)
          ? response.data.results
          : [];
      const group = rawResults.find(
        (item: any) => item?.partNumber === rootPartNumber,
      );
      return Array.isArray(group?.documents) ? group.documents : [];
    },
    {
      enabled: Boolean(rootPartNumber),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  const rows: CboDocument[] = Array.isArray(data) ? data : [];
  const normalizedTargetPartNumber = (partNumber || "").trim();
  const sortedRows = React.useMemo(() => {
    const clone = [...rows];
    clone.sort((a, b) => {
      const aPart = (a.partNumber || "").trim();
      const bPart = (b.partNumber || "").trim();
      const aIsTarget = aPart === normalizedTargetPartNumber;
      const bIsTarget = bPart === normalizedTargetPartNumber;
      if (aIsTarget && !bIsTarget) return -1;
      if (!aIsTarget && bIsTarget) return 1;
      return aPart.localeCompare(bPart);
    });
    return clone;
  }, [rows, normalizedTargetPartNumber]);
  const rowsWithPartSpan = withPartNumberRowSpan(sortedRows);

  if (isFetching) {
    return (
      <Box p={3} minW="300px">
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} minW="300px">
        <Text fontSize="xs" color="red.500">
          Failed to load CBO details
        </Text>
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Box p={3} minW="300px">
        <Text fontSize="xs" color="gray.600">
          No CBO details found.
        </Text>
      </Box>
    );
  }

  const totals = rows.reduce(
    (acc, row) => ({
      cbo: acc.cbo + Number(row.totalCBOQuantity || 0),
      dispatched: acc.dispatched + getDispatchTotal(row),
      invoiced: acc.invoiced + Number(row.invoiceQuantity || 0),
      underPicking: acc.underPicking + Number(row.underPickingQuantity || 0),
      allocated: acc.allocated + Number(row.allocatedQuantity || 0),
      backOrder: acc.backOrder + Number(row.backOrderQuantity || 0),
      advanceOrder: acc.advanceOrder + Number(row.advancedOrderQuantity || 0),
    }),
    {
      cbo: 0,
      dispatched: 0,
      invoiced: 0,
      underPicking: 0,
      allocated: 0,
      backOrder: 0,
      advanceOrder: 0,
    },
  );

  return (
    <Box p={2}>
      <Text fontSize="xs" color="gray.600" mb={2}>
        Part Number: {partNumber || "—"}
      </Text>
      <TableContainer overflowX="auto" maxW="100%" maxH="400px" overflowY="auto">
        <Table size="sm" variant="unstyled" minW="760px">
          <Thead bg="gray.100">
            <Tr>
              <Th textAlign="left" fontSize="xs" color="gray.700">
                Part Number
              </Th>
              <Th textAlign="left" fontSize="xs" color="gray.700">
                Supplier Name
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                CBO
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Dispatched
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Invoiced
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Under Picking
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Allocated
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Back Order
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Advance Order
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rowsWithPartSpan.map(({ row, showPartNumber, rowSpan }, idx) => (
              <Tr
                key={`${row.supplierBusinessName}-${idx}`}
                bg={idx % 2 ? "gray.50" : "white"}
              >
                {showPartNumber ? (
                  <Td
                    fontWeight="700"
                    color="gray.700"
                    fontSize="sm"
                    rowSpan={rowSpan}
                    verticalAlign="top"
                  >
                    {row.partNumber || "—"}
                  </Td>
                ) : null}
                <Td fontWeight="700" color="gray.700" fontSize="sm">
                  {row.supplierBusinessName}
                </Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  color="#2F9E77"
                  fontSize="xs"
                >
                  {Number(row.totalCBOQuantity || 0)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {getDispatchedDisplay(row)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {Number(row.invoiceQuantity || 0)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {Number(row.underPickingQuantity || 0)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {Number(row.allocatedQuantity || 0)}
                </Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  color="#C95A8C"
                  fontSize="xs"
                >
                  {Number(row.backOrderQuantity || 0)}
                </Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  color="#64AFA0"
                  fontSize="xs"
                >
                  {Number(row.advancedOrderQuantity || 0)}
                </Td>
              </Tr>
            ))}
            <Tr bg="gray.100">
              <Td fontWeight="800" fontSize="sm" color="gray.700">
                Total
              </Td>
              <Td />
              <Td
                textAlign="center"
                fontWeight="800"
                color="#2F9E77"
                fontSize="xs"
              >
                {totals.cbo}
              </Td>
              <Td textAlign="center" fontWeight="800" fontSize="xs">
                {totals.dispatched}
              </Td>
              <Td textAlign="center" fontWeight="800" fontSize="xs">
                {totals.invoiced}
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#A8792A"
                fontSize="xs"
              >
                {totals.underPicking}
              </Td>
              <Td textAlign="center" fontWeight="800" fontSize="xs">
                {totals.allocated}
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#C95A8C"
                fontSize="xs"
              >
                {totals.backOrder}
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#64AFA0"
                fontSize="xs"
              >
                {totals.advanceOrder}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CboDetailsPopoverContent;
